import { t } from 'i18next';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStrategicImperativeListConfig from './StrategicImperativeList.config';
import {
  mayaCustomerInitiativeCreateRoute,
  mayaStrategicImperativeCreateRoute,
  mayaStrategicImperativeDuplicateRoute,
  mayaStrategicImperativeEditRoute,
} from 'basics/constants/routes.constants';
import { StrategicImperative } from 'generated/maya.types';
import { PAGINATION_PAGE_SIZE } from 'modules/maya/basics/constants/common.constant';
import useGetAllStrategicImperativeQuery from 'modules/maya/graphql/queries/getAllStrategicImperatives';

const useStrategicImperativeList = () => {
  const navigate = useNavigate();

  const [siTitleFilter, setSiTitleFilter] = useState<(string)>();
  const [strategicImperatives, setStrategicImperatives] = useState<StrategicImperative[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);
  const [rowCountState, setRowCountState] = useState(0);
  const urlParams = new URLSearchParams(window.location.search);
  const year = urlParams.get('year');

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const queryFilters = useMemo(
    () => ({
      title: siTitleFilter,
      year: year ? parseInt(year, 10) : undefined,
    }),
    [siTitleFilter, year],
  );

  const { data, loading, count } = useGetAllStrategicImperativeQuery(queryFilters, queryOptions);

  useEffect(() => {
    setRowCountState((prevRowCountState) => (count !== undefined ? count : prevRowCountState));
  }, [count, setRowCountState]);

  useEffect(() => {
    if (!loading) {
      setStrategicImperatives(data);
    }
  }, [data, loading]);

  const newStrategicImperativeClick = useCallback(() => {
    navigate(mayaStrategicImperativeCreateRoute);
  }, [navigate]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const handleChangeSiTitle = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSiTitleFilter(event.target.value);
  };
  const debouncedHandleChangeSiTitle = useMemo(
    () => debounce(handleChangeSiTitle, 750),
    [],
  );

  const editSiCallback = async (ciId: string) => {
    navigate(`${mayaStrategicImperativeEditRoute}?id=${ciId}`);
  };

  const duplicateSiCallback = async (ciId: string) => {
    navigate(`${mayaStrategicImperativeDuplicateRoute}?sourceId=${ciId}`);
  };

  const addChildAction = async (ciId: string) => {
    navigate(`${mayaCustomerInitiativeCreateRoute}?parentId=${ciId}`);
  };

  const { strategicImperativeListColumnConfig, siTitleFieldConfig } = useStrategicImperativeListConfig(
    editSiCallback,
    duplicateSiCallback,
    addChildAction,
  );

  const menuStrategicImperativeLabel = t('maya_main_menu_strategic_imperative_label');

  const newElementButton = {
    newElementLabel: t('maya_list_new_element_label', { elementTitle: menuStrategicImperativeLabel }),
    newElementClick: newStrategicImperativeClick,
  };

  return {
    strategicImperativeListColumnConfig,
    siTitleFieldConfig,
    debouncedHandleChangeSiTitle,
    newElementButton,
    menuStrategicImperativeLabel,
    onPageChange,
    onPageSizeChange,
    rowCountState,
    strategicImperatives,
    loading,
  };
};

export default useStrategicImperativeList;
